<template>
  <div>
    <section class="activity-stage find-the-letter">
      <h2 class="find-the-letter-instructions">
        Find and select every
        <span v-if="activityData && currentRound.letter" :class="`color${letterColor}`">{{
          currentRound.letter
        }}</span
        >.
      </h2>

      <section class="find-the-letters-container">
        <div class="letters-to-type-container" v-for="word of words" :key="word.id">
          <aside>
            <figure :class="`image word-${word.id}`">
              <img :src="word.wordImage.image.src" />
            </figure>
          </aside>

          <div class="letter-container">
            <span
              class="letter"
              v-for="(letter, index) in word.letters"
              :class="letter.classes"
              :key="`${word.id}-${index}`"
              @click="handleLetterClick(letter)"
            >
              {{ letter.letter }}
            </span>
          </div>
        </div>
      </section>
    </section>

    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'Find The Letter',
      voice: 'ma',
      words: []
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    letterColor() {
      const upperCase = this.currentRound.letter.toUpperCase();

      if (['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'].includes(upperCase)) return 'Yellow';
      if (['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'].includes(upperCase)) return 'Green';
      if (['Z', 'X', 'C', 'V', 'B', 'N', 'M'].includes(upperCase)) return 'Blue';

      return '';
    },

    foundAllLetters() {
      if (!this.words.length) return false;

      return !this.words.some(word =>
        word.letters.find(letter => letter.isTarget && !letter.classes.clicked)
      );
    },

    typedAllLetters() {
      return !this.words.some(word =>
        word.letters.find(letter => letter.isTarget && !letter.typed)
      );
    }
  },

  methods: {
    getFilename(letter, numericSuffix) {
      return numericSuffix
        ? `${this.voiceInstructionsPrefix}/findtheletter-${letter}-${numericSuffix}-${this.voice}.mp3`
        : `${this.voiceInstructionsPrefix}/findtheletternth-${letter}-${this.voice}.mp3`;
    },

    async loadActivity() {
      const roundSetup = this.getActivityState();

      this.createRounds(roundSetup);
      await this.loadImages();
      await this.loadInstructions();
    },

    async loadImages() {
      this.rounds.forEach(({ steps }) => {
        steps.forEach(({ wordImage }) => {
          this.queueImage(wordImage.image.src);
        });
      });

      return this.downloadImages();
    },

    async loadInstructions() {
      // CSC - most of the female voice audio is missing for this activity, so hardcoding the male voice for now
      // this.voice = this.getRandomVoice();
      this.rounds.forEach(({ letter }) => {
        this.queueAudio(this.getFilename(letter, 1));
        this.queueAudio(this.getFilename(letter, 2));
        this.queueAudio(this.getFilename(letter));
      });

      return this.downloadAudio();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds: rounds.map(({ steps, ...round }) => ({
          ...round,
          steps: steps.map(step => ({
            ...step,
            answer: round.letter
          }))
        })),
        instructions: {
          written: '',
          voice: [''],
          delay: 4
        }
      };
    },

    startActivity() {
      const timeline = new TimelineLite();

      timeline
        .call(this.setNewRound)
        .call(this.hideKeyboard)
        .call(this.errorLoggerStart)
        .to('.activity-stage', 1, { opacity: 1 });
    },

    getKeyboardConfig() {
      const {
        keyboard: { characters, keyboardType, roofs }
      } = this.activityData;
      const isTouchHold = !!characters;
      const showTargets = this.isMobile && !(this.ipadWithoutExternalKeyboard && isTouchHold);

      return {
        showTargets,
        keyboardType,
        roofs
      };
    },

    getCurrentRoundState() {
      const targetLetter = this.currentRound.letter.toLowerCase();

      const result = this.currentRound.steps.map(({ id, word, wordImage }) => ({
        id,
        wordImage,
        letters: word.split('').map(letter => ({
          letter,
          classes: {
            clicked: false,
            [`color${this.letterColor}`]: false
          },
          isTarget: letter.toLowerCase() === targetLetter,
          typed: false
        }))
      }));

      if (this.foundAllLetters) {
        const firstWord = result[0];
        const firstLetterToBeTyped = firstWord.letters.find(letter => letter.isTarget);

        if (!firstLetterToBeTyped) return result;

        Object.assign(firstLetterToBeTyped.classes, {
          [`color${this.letterColor}`]: true
        });
      }

      return result;
    },

    setNewRound() {
      this.setupNewRound();
      this.cache.get(this.getFilename(this.currentRound.letter, 1)).play();
      this.words = this.getCurrentRoundState();
    },

    handleLetterClick(letterObj) {
      if (letterObj.classes.clicked) return;

      if (letterObj.isTarget) {
        Object.assign(letterObj.classes, {
          clicked: true,
          [`color${this.letterColor}`]: true
        });

        this.sndBell();

        if (this.foundAllLetters) {
          TweenMax.delayedCall(1, () => {
            this.setUpTyping();
          });
        }
      } else {
        this.sndError();
      }
    },

    setUpTyping() {
      const filename = this.getFilename(this.currentRound.letter, this.isTouchHold ? 2 : undefined);
      this.cache.get(filename).play();

      this.enableKeyboard();
      this.showKeyboard({ enable: true });

      this.words = this.getCurrentRoundState();
    },

    moveSelectedLetter() {
      let foundTyped = false;
      let foundNext = false;

      this.words = this.words.map(word => {
        word.letters.forEach(letter => {
          if (letter.isTarget && !letter.typed) {
            if (!foundTyped) {
              letter.typed = true;
              letter.classes = {
                ...letter.classes,
                [`color${this.letterColor}`]: false
              };
              foundTyped = true;
            } else if (!foundNext) {
              letter.classes = {
                ...letter.classes,
                [`color${this.letterColor}`]: true
              };
              foundNext = true;
            }
          }
        });
        return word;
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.moveSelectedLetter();

      if (this.typedAllLetters) {
        this.sndBell();
        this.hideKeyboard();
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();

      if (this.isLastRound) {
        timeline.delay(1).call(this.completeActivity);
      } else {
        timeline
          .delay(1)
          .to('.activity-stage', 0.5, { opacity: 0 })
          .call(this.setNewRound)
          .call(this.$nextTick, [() => timeline.to('.activity-stage', 0.5, { opacity: 1 })]);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.find-the-letter-instructions {
  span {
    padding: 3px;
  }
}

.find-the-letters-container {
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
}

.letters-to-type-container {
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;

  .image {
    min-width: 270px;
    min-height: 182px;
  }
}

.letter-container {
  display: flex;
  flex-flow: row nowrap;

  span {
    padding: 3px;
    cursor: pointer;
  }
}

.colorYellow {
  background-color: yellow;
}
.colorGreen {
  background-color: lightgreen;
}
.colorBlue {
  background-color: lightblue;
}
</style>
